import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import ProjectTracker from "@/components/ProjectTracker";
import { ArrowLeft, Link as LinkIcon, Receipt } from "lucide-react";
import { toast } from "sonner";

async function viewReceipt(orderId) {
  try {
    const { data } = await api.get(`/orders/${orderId}/receipt.pdf`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const w = window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    if (!w) toast.error("Popup blocked — allow popups to view receipt.");
  } catch {
    toast.error("Failed to load receipt");
  }
}

export default function OrderDetail() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/orders/${orderId}`)
      .then((r) => setOrder(r.data))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (loading)
    return (
      <div className="min-h-screen bg-black text-white">
        <Nav />
        <div className="pt-32 px-6 text-[#a0a0a0] max-w-7xl mx-auto">Loading…</div>
      </div>
    );
  if (!order)
    return (
      <div className="min-h-screen bg-black text-white">
        <Nav />
        <div className="pt-32 px-6 text-[#a0a0a0] max-w-7xl mx-auto">Order not found.</div>
      </div>
    );

  return (
    <div className="min-h-screen bg-black text-white">
      <Nav />
      <div className="pt-28 pb-24 px-6 sm:px-10">
        <div className="max-w-6xl mx-auto">
          <Link
            to="/dashboard"
            data-testid="back-to-dashboard"
            className="inline-flex items-center gap-2 text-sm text-[#a0a0a0] hover:text-white btn-anim"
          >
            <ArrowLeft size={14} /> Back to dashboard
          </Link>

          <div className="mt-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#a0a0a0]">
                Order #{order.order_id.slice(-6)}
              </div>
              <h1 className="text-4xl font-bold tracking-tighter mt-2" data-testid="order-title-h1">
                {Array.isArray(order.services) && order.services.length
                  ? (order.primary_service_name ||
                     order.services[0].service_id.replace(/_/g, " "))
                  : `${order.package_clips} pieces · ${order.editing_style}`}
              </h1>
              <div className="text-[#a0a0a0] mt-2 text-sm">
                {Array.isArray(order.services) && order.services.length > 1 && (
                  <span className="mr-2">
                    {order.services.length} services ·{" "}
                    {order.services.reduce((s, x) => s + (x.quantity || 0), 0)} pieces ·
                  </span>
                )}
                Placed {new Date(order.created_at).toLocaleString()} · ETA{" "}
                {new Date(order.estimated_delivery).toLocaleDateString()}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="squircle-sm border border-[#2b2b2b] bg-[#111] px-4 py-2 text-sm">
                Total: <span className="font-semibold">${order.price?.total?.toFixed(2)}</span>
              </div>
              <button
                onClick={() => viewReceipt(order.order_id)}
                data-testid="order-receipt-btn"
                className="squircle-sm border border-[#2b2b2b] bg-[#111] px-4 py-2 text-sm inline-flex items-center gap-1.5 hover:bg-[#161616] btn-anim"
              >
                <Receipt size={14} /> Receipt
              </button>
            </div>
          </div>

          <div className="mt-10 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <ProjectTracker order={order} />

              {order.preview_images?.length > 0 && (
                <div className="mt-6 squircle border border-[#2b2b2b] bg-[#111] p-8">
                  <h3 className="text-xl font-semibold mb-6">Previews</h3>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {order.preview_images.map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        alt="preview"
                        className="w-full h-40 object-cover squircle-sm border border-[#2b2b2b] grayscale"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            <aside className="space-y-6">
              <div className="squircle border border-[#2b2b2b] bg-[#111] p-8">
                <h3 className="text-lg font-semibold mb-4">Details</h3>
                {Array.isArray(order.services) && order.services.length ? (
                  <>
                    {order.services.map((s, i) => (
                      <div key={i} className="mb-4 last:mb-0 pb-4 last:pb-0 border-b border-[#1e1e1e] last:border-b-0">
                        <div className="text-sm font-medium">
                          {s.service_id.replace(/_/g, " ")}
                        </div>
                        <div className="text-xs text-[#a0a0a0] mt-0.5">
                          {s.quantity} · {s.style_id}
                          {s.footage_minutes ? ` · ~${s.footage_minutes} min` : ""}
                        </div>
                      </div>
                    ))}
                    <Detail label="Subscription" value={order.subscription} />
                    <Detail label="Stage" value={order.stage} />
                  </>
                ) : (
                  <>
                    <Detail label="Source" value={order.source_type} />
                    <Detail label="Package" value={`${order.package_clips} pieces`} />
                    <Detail label="Style" value={order.editing_style} />
                    <Detail label="Subscription" value={order.subscription} />
                    <Detail label="Stage" value={order.stage} />
                  </>
                )}
              </div>

              <div className="squircle border border-[#2b2b2b] bg-[#111] p-8">
                <h3 className="text-lg font-semibold mb-4">Source links</h3>
                <ul className="space-y-2">
                  {(Array.isArray(order.services) && order.services.length
                    ? order.services.flatMap((s) => s.source_links || [])
                    : order.source_links || []
                  ).map((l, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-[#a0a0a0] truncate">
                      <LinkIcon size={14} />
                      <span className="truncate">{l}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {order.extras?.length > 0 && (
                <div className="squircle border border-[#2b2b2b] bg-[#111] p-8">
                  <h3 className="text-lg font-semibold mb-4">Extras</h3>
                  <ul className="space-y-1 text-sm text-[#a0a0a0]">
                    {order.extras.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}

              {order.notes && (
                <div className="squircle border border-[#2b2b2b] bg-[#111] p-8">
                  <h3 className="text-lg font-semibold mb-4">Notes</h3>
                  <p className="text-sm text-[#a0a0a0] whitespace-pre-wrap">{order.notes}</p>
                </div>
              )}
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#1e1e1e] last:border-0">
      <div className="text-xs text-[#a0a0a0] uppercase tracking-widest">{label}</div>
      <div className="text-sm">{value}</div>
    </div>
  );
}
